import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router'
import { publicPost } from '../api/http'
import { session } from '../api/session'
import type { User } from '../api/types'
import { Button } from '../components/ui/Button'
import { Field, Input } from '../components/ui/Field'
import { IconEye, IconEyeOff, IconLogo } from '../components/icons'

type SessionResponse = { accessToken: string; user: User }

function AuthLayout({ title, subtitle, children }: { title: string; subtitle?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex min-h-full flex-col justify-center items-center px-4 pt-12 pb-[calc(3rem+env(safe-area-inset-bottom,0px))] sm:px-6">
      <div className="w-full max-w-[360px] mx-auto">
        <div className="mb-8">
          <IconLogo className="w-11 h-11 rounded-xl mb-5 shadow-xs object-cover" />
          <h1 className="font-display text-2xl font-bold tracking-tight text-ink-100">{title}</h1>
          {subtitle && <p className="mt-1.5 text-sm text-ink-400">{subtitle}</p>}
        </div>
        <div>{children}</div>
      </div>
    </div>
  )
}

function useSubmit() {
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try { await fn() } catch (err) { setError((err as Error).message) } finally { setBusy(false) }
  }
  return { error, busy, run }
}

export function LoginPage({ openRegistration = false, sso = null }: { openRegistration?: boolean; sso?: string | null }) {
  const { t } = useTranslation()
  const ssoError = new URLSearchParams(window.location.search).get('sso_error')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const { error, busy, run } = useSubmit()
  const submit = (e: FormEvent) => {
    e.preventDefault()
    void run(async () => session.signedIn(await publicPost<SessionResponse>('/api/auth/login', { email, password })))
  }
  return (
    <AuthLayout title={t('auth.loginTitle')} subtitle={t('auth.loginSubtitle')}>
      {sso && (
        <div className="mb-5 space-y-4">
          <button
            type="button"
            onClick={() => { window.location.href = '/api/auth/oidc/start' }}
            className="w-full h-11 px-4 rounded-xl border border-cream-300 bg-cream-50 hover:bg-cream-200 active:bg-cream-300 text-ink-200 text-sm font-medium transition-colors shadow-xs flex items-center justify-center gap-2.5 cursor-pointer"
          >
            <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.8-2.4 3.66v3.05h3.89c2.28-2.1 3.65-5.2 3.65-9.15z"/>
              <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.89-3.05c-1.07.72-2.45 1.16-4.04 1.16-3.12 0-5.77-2.1-6.72-4.93H1.24v3.15C3.26 21.4 7.34 24 12 24z"/>
              <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.24C.45 8.15 0 9.92 0 12s.45 3.85 1.24 5.42l4.04-3.15z"/>
              <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.34 0 3.26 2.6 1.24 6.58l4.04 3.15c.95-2.83 3.6-4.98 6.72-4.98z"/>
            </svg>
            <span className="truncate">{t('auth.ssoButton', { label: sso })}</span>
          </button>
          <div className="flex items-center gap-3">
            <span className="h-px flex-1 bg-cream-300" />
            <span className="text-xs text-ink-500 font-medium">{t('auth.orEmail')}</span>
            <span className="h-px flex-1 bg-cream-300" />
          </div>
        </div>
      )}
      <form onSubmit={submit} className="space-y-4">
        <Field label={t('auth.email')}>
          {id => (
            <Input
              id={id}
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="h-11 rounded-xl shadow-xs"
            />
          )}
        </Field>
        <Field label={t('auth.password')}>
          {id => (
            <div className="relative">
              <Input
                id={id}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="h-11 rounded-xl pr-10 shadow-xs"
              />
              <button
                type="button"
                onClick={() => setShowPassword(p => !p)}
                className="absolute right-0 top-0 h-11 px-3 text-ink-400 hover:text-ink-100 flex items-center justify-center transition-colors cursor-pointer"
                title={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
              >
                {showPassword ? <IconEyeOff className="w-4 h-4" /> : <IconEye className="w-4 h-4" />}
              </button>
            </div>
          )}
        </Field>
        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        {ssoError && <p role="alert" className="text-sm text-red-700">{t('auth.ssoFailed')}: {ssoError}</p>}
        <Button type="submit" variant="strong" className="w-full h-11 rounded-xl shadow-xs" loading={busy}>
          {t('auth.login')}
        </Button>
      </form>
      {openRegistration && (
        <p className="mt-7 text-center text-xs text-ink-400">
          {t('auth.noAccount')}{' '}
          <Link to="/signup" className="font-semibold text-ink-100 hover:underline">
            {t('auth.signup')}
          </Link>
        </p>
      )}
    </AuthLayout>
  )
}

function AccountForm({ submitLabel, fixedEmail, onSubmit }: { submitLabel: string; fixedEmail?: string | null; onSubmit(v: { name: string; email: string; password: string }): Promise<void> }) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [email, setEmail] = useState(fixedEmail ?? '')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const { error, busy, run } = useSubmit()
  const mismatch = confirm.length > 0 && confirm !== password
  return (
    <form
      className="space-y-4"
      onSubmit={e => { e.preventDefault(); if (!mismatch) void run(() => onSubmit({ name, email, password })) }}
    >
      <Field label={t('auth.name')}>
        {id => <Input id={id} required autoComplete="name" value={name} onChange={e => setName(e.target.value)} className="h-11 rounded-xl shadow-xs" />}
      </Field>
      <Field label={t('auth.email')}>
        {id => <Input id={id} type="email" required autoComplete="email" readOnly={Boolean(fixedEmail)} value={email} onChange={e => setEmail(e.target.value)} className="h-11 rounded-xl shadow-xs" />}
      </Field>
      <Field label={t('auth.password')} hint={t('auth.passwordHint')}>
        {id => (
          <div className="relative">
            <Input
              id={id}
              type={showPassword ? 'text' : 'password'}
              required
              minLength={10}
              autoComplete="new-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="h-11 rounded-xl pr-10 shadow-xs"
            />
            <button
              type="button"
              onClick={() => setShowPassword(p => !p)}
              className="absolute right-0 top-0 h-11 px-3 text-ink-400 hover:text-ink-100 flex items-center justify-center transition-colors cursor-pointer"
              title={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
              aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
            >
              {showPassword ? <IconEyeOff className="w-4 h-4" /> : <IconEye className="w-4 h-4" />}
            </button>
          </div>
        )}
      </Field>
      <Field label={t('auth.confirmPassword')} error={mismatch ? t('auth.mismatch') : undefined}>
        {id => <Input id={id} type={showPassword ? 'text' : 'password'} required autoComplete="new-password" value={confirm} onChange={e => setConfirm(e.target.value)} className="h-11 rounded-xl shadow-xs" />}
      </Field>
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      <Button type="submit" variant="strong" className="w-full h-11 rounded-xl shadow-xs" loading={busy} disabled={mismatch}>{submitLabel}</Button>
    </form>
  )
}

export function SetupPage() {
  const { t } = useTranslation()
  return (
    <AuthLayout title={t('auth.setupTitle')} subtitle={t('auth.setupSubtitle')}>
      <AccountForm submitLabel={t('auth.createAdmin')} onSubmit={async v => session.signedIn(await publicPost<SessionResponse>('/api/setup', v))} />
    </AuthLayout>
  )
}

export function InvitePage() {
  const { t } = useTranslation()
  const { token = '' } = useParams()
  const [invite, setInvite] = useState<{ email: string | null } | null>(null)
  const [invalid, setInvalid] = useState(false)
  useEffect(() => {
    fetch(`/api/invites/${encodeURIComponent(token)}`)
      .then(async r => (r.ok ? setInvite(await r.json()) : setInvalid(true)))
      .catch(() => setInvalid(true))
  }, [token])
  if (invalid) {
    return (
      <AuthLayout title={t('auth.inviteInvalid')} subtitle={t('auth.inviteInvalidBody')}>
        <Link className="text-sm font-semibold text-ink-100 underline" to="/login">{t('auth.toLogin')}</Link>
      </AuthLayout>
    )
  }
  if (!invite) return null
  return (
    <AuthLayout title={t('auth.inviteTitle')} subtitle={t('auth.inviteSubtitle')}>
      <AccountForm
        submitLabel={t('auth.register')}
        fixedEmail={invite.email}
        onSubmit={async v => session.signedIn(await publicPost<SessionResponse>('/api/auth/register', { ...v, token }))}
      />
    </AuthLayout>
  )
}

export function SignupPage() {
  const { t } = useTranslation()
  return (
    <AuthLayout title={t('auth.signupTitle')} subtitle={t('auth.signupSubtitle')}>
      <AccountForm submitLabel={t('auth.register')} onSubmit={async v => session.signedIn(await publicPost<SessionResponse>('/api/auth/register', v))} />
      <p className="mt-7 text-center text-xs text-ink-400">{t('auth.haveAccount')} <Link to="/login" className="font-semibold text-ink-100 hover:underline">{t('auth.login')}</Link></p>
    </AuthLayout>
  )
}
